/** Right drawer (440, e4) with a focus trap; Esc closes and focus returns to the invoker (P10 acceptance 2). At 768 px and narrower it is a full-height bottom sheet (theme.css) and the page behind does not scroll. */
import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { useFocusTrap } from "./focusTrap";
import { useScrollLock } from "./GxSheet";

export function Drawer({ title, context, onClose, children, footer, width }: {
  /** A wider panel for tables (any CSS length); the default is the standard drawer width. */
  readonly width?: string;
  readonly title: string; readonly context?: ReactNode; readonly onClose: () => void; readonly children: ReactNode; readonly footer?: ReactNode;
}) {
  const panel = useRef<HTMLElement>(null);

  const onKeyDown = useFocusTrap(panel);
  useScrollLock();

  // Esc closes wherever focus is: a click on a starter or a button that then disappears leaves focus on the page, not in the panel.
  useEffect(() => {
    const escape = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) onClose(); };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  return (
    <>
      <div className="gx-scrim" onClick={onClose} aria-hidden="true" />
      <aside ref={panel} className="gx-drawer" style={width === undefined ? undefined : ({ "--drawer-w": width } as CSSProperties)} role="dialog" aria-modal="true" aria-label={title} onKeyDown={onKeyDown}>
        <div className="gx-drawer-h">
          <h2 className="gx-h2">{title}</h2>
          {context}
          <button type="button" className="gx-x" aria-label={`Close ${title}`} onClick={onClose}>×</button>
        </div>
        <div className="gx-drawer-b">{children}</div>
        {footer === undefined ? null : <div className="gx-drawer-f">{footer}</div>}
      </aside>
    </>
  );
}
